import { useRef, useState, type PointerEvent } from 'react'
import { motion, useMotionValue, useSpring, useTransform } from 'motion/react'
import { ASSET_ICON } from '../constants'

interface LogoEmblemProps {
  size?: 'sm' | 'hero'
  className?: string
  interactive?: boolean
}

export function LogoEmblem({ size = 'sm', className = '', interactive = true }: LogoEmblemProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(false)
  const mx = useMotionValue(0)
  const my = useMotionValue(0)
  const sx = useSpring(mx, { stiffness: 220, damping: 18 })
  const sy = useSpring(my, { stiffness: 220, damping: 18 })
  const rotateX = useTransform(sy, [-0.5, 0.5], interactive ? [14, -14] : [0, 0])
  const rotateY = useTransform(sx, [-0.5, 0.5], interactive ? [-16, 16] : [0, 0])
  const glareX = useTransform(sx, [-0.5, 0.5], [12, 88])
  const glareY = useTransform(sy, [-0.5, 0.5], [18, 82])
  const glareBg = useTransform(
    [glareX, glareY],
    ([x, y]) =>
      `radial-gradient(circle at ${x}% ${y}%, rgba(255,255,255,0.55), transparent 55%)`,
  )

  const dim = size === 'hero' ? 120 : 40
  const plateClass =
    size === 'hero' ? 'cr-emblem-plate cr-emblem-plate--hero' : 'cr-emblem-plate cr-emblem-plate--sm'

  const onMove = (e: PointerEvent) => {
    if (!interactive) return
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    mx.set((e.clientX - rect.left) / rect.width - 0.5)
    my.set((e.clientY - rect.top) / rect.height - 0.5)
  }

  const reset = () => {
    mx.set(0)
    my.set(0)
    setActive(false)
  }

  return (
    <motion.div
      ref={ref}
      className={`cr-logo-live ${plateClass} ${className}`.trim()}
      style={{
        rotateX,
        rotateY,
        transformStyle: 'preserve-3d',
      }}
      animate={
        interactive
          ? active
            ? { scale: 1.06 }
            : { scale: 1, y: [0, -5, 0] }
          : { scale: 1, y: 0 }
      }
      transition={
        active
          ? { type: 'spring', stiffness: 320, damping: 18 }
          : interactive
            ? { y: { duration: 3.6, repeat: Infinity, ease: 'easeInOut' }, scale: { duration: 0.25 } }
            : { duration: 0.2 }
      }
      onPointerEnter={() => interactive && setActive(true)}
      onPointerMove={onMove}
      onPointerLeave={reset}
      whileTap={interactive ? { scale: 0.96 } : undefined}
    >
      <img src={ASSET_ICON} width={dim} height={dim} alt="" draggable={false} />
      <motion.span className="cr-logo-glare" style={{ background: glareBg }} aria-hidden />
      <span className="cr-logo-ring" aria-hidden />
    </motion.div>
  )
}
